import React from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import Layout from "../components/layout/Layout";
import { ArrowLeft, Shield } from "lucide-react";

export default function PrivacyPage() {
  return (
    <Layout>
      <div id="privacy_page" className="min-h-screen bg-neutral-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-neutral-400 hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-lg font-extralight">На главную</span>
            </Link>

            <div className="flex items-center gap-4 mb-10">
              <div className="w-12 h-12 rounded-md bg-accent-500/10 flex items-center justify-center">
                <Shield className="w-6 h-6 text-accent-400" />
              </div>
              <div>
                <h1 className="text-3xl lg:text-4xl font-medium text-white">
                  Политика конфиденциальности
                </h1>
                <p className="text-lg font-extralight text-neutral-500 mt-1">
                  Редакция от 19 января 2025 года
                </p>
              </div>
            </div>

            <div className="space-y-10 text-lg font-extralight text-neutral-300 leading-relaxed">
              <section>
                <h2 className="text-xl font-medium text-white mb-4">1. Общие положения</h2>
                <p className="mb-3">
                  1.1. Настоящая Политика конфиденциальности (далее — Политика) определяет порядок
                  сбора, хранения, обработки и защиты персональных данных Пользователей информационной
                  платформы "Обстучим" (далее — Платформа).
                </p>
                <p className="mb-3">
                  1.2. Политика разработана в соответствии с Федеральным законом от 27.07.2006
                  N 152-ФЗ "О персональных данных" и иными нормативными правовыми актами
                  Российской Федерации.
                </p>
                <p>
                  1.3. Используя Платформу, Пользователь выражает согласие с условиями настоящей
                  Политики. В случае несогласия Пользователь обязан прекратить использование Платформы.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium text-white mb-4">2. Собираемые данные</h2>
                <p className="mb-3">
                  2.1. Платформа может собирать следующие категории персональных данных:
                </p>
                <ul className="list-none space-y-2 ml-6 mb-3">
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-accent-500 mt-1.5 flex-shrink-0">—</span>
                    фамилия, имя, отчество
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-accent-500 mt-1.5 flex-shrink-0">—</span>
                    адрес электронной почты
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-accent-500 mt-1.5 flex-shrink-0">—</span>
                    данные профиля: фотография, должность, сведения о профессиональном опыте
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-accent-500 mt-1.5 flex-shrink-0">—</span>
                    информация об устройстве и браузере (IP-адрес, тип браузера, операционная система)
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-accent-500 mt-1.5 flex-shrink-0">—</span>
                    данные о действиях на Платформе (просмотренные страницы, время визита)
                  </li>
                </ul>
                <p>
                  2.2. Платформа использует файлы cookie для обеспечения корректной работы сервиса,
                  аналитики и персонализации. Пользователь может управлять cookie через настройки браузера.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium text-white mb-4">3. Цели обработки данных</h2>
                <p className="mb-3">
                  3.1. Персональные данные обрабатываются в следующих целях:
                </p>
                <ul className="list-none space-y-2 ml-6">
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-accent-500 mt-1.5 flex-shrink-0">—</span>
                    регистрация и идентификация Пользователя на Платформе
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-accent-500 mt-1.5 flex-shrink-0">—</span>
                    обеспечение функционирования Платформы и предоставление доступа к сервисам
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-accent-500 mt-1.5 flex-shrink-0">—</span>
                    коммуникация с Пользователем, в том числе отправка уведомлений
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-accent-500 mt-1.5 flex-shrink-0">—</span>
                    улучшение качества работы Платформы и аналитика
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-accent-500 mt-1.5 flex-shrink-0">—</span>
                    выполнение требований законодательства Российской Федерации
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-medium text-white mb-4">4. Хранение и защита данных</h2>
                <p className="mb-3">
                  4.1. Персональные данные хранятся на защищённых серверах с применением современных
                  средств криптографической защиты информации.
                </p>
                <p className="mb-3">
                  4.2. Администрация принимает организационные и технические меры для защиты
                  персональных данных от несанкционированного доступа, уничтожения, изменения,
                  блокирования, копирования и распространения.
                </p>
                <p>
                  4.3. Персональные данные хранятся в течение срока, необходимого для достижения
                  целей обработки, но не дольше срока, установленного законодательством РФ.
                  По истечении указанного срока данные удаляются или обезличиваются.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium text-white mb-4">5. Передача данных третьим лицам</h2>
                <p className="mb-3">
                  5.1. Администрация не передаёт персональные данные третьим лицам, за исключением случаев:
                </p>
                <ul className="list-none space-y-2 ml-6">
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-accent-500 mt-1.5 flex-shrink-0">—</span>
                    наличия явно выраженного согласия Пользователя
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-accent-500 mt-1.5 flex-shrink-0">—</span>
                    требования законодательства Российской Федерации (по запросу уполномоченных органов)
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-accent-500 mt-1.5 flex-shrink-0">—</span>
                    необходимости для оказания услуг Пользователю (обработка платежей)
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-medium text-white mb-4">6. Права Пользователя</h2>
                <p className="mb-3">
                  6.1. Пользователь имеет право:
                </p>
                <ul className="list-none space-y-2 ml-6">
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-accent-500 mt-1.5 flex-shrink-0">—</span>
                    получить информацию об обработке своих персональных данных
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-accent-500 mt-1.5 flex-shrink-0">—</span>
                    потребовать уточнения, блокирования или уничтожения персональных данных
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-accent-500 mt-1.5 flex-shrink-0">—</span>
                    отозвать согласие на обработку персональных данных, направив соответствующий запрос Администрации
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-accent-500 mt-1.5 flex-shrink-0">—</span>
                    обратиться в уполномоченный орган по защите прав субъектов персональных данных (Роскомнадзор)
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-medium text-white mb-4">7. Рассылка и маркетинговые сообщения</h2>
                <p className="mb-3">
                  7.1. Пользователь может дать согласие на получение информационной рассылки при регистрации.
                  Данное согласие является добровольным и не влияет на возможность использования Платформы.
                </p>
                <p>
                  7.2. Пользователь вправе в любой момент отказаться от получения рассылки,
                  воспользовавшись ссылкой для отписки в письме или обратившись к Администрации.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium text-white mb-4">8. Файлы cookie</h2>
                <p className="mb-3">
                  8.1. Платформа использует следующие виды файлов cookie:
                </p>
                <ul className="list-none space-y-2 ml-6 mb-3">
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-accent-500 mt-1.5 flex-shrink-0">—</span>
                    технические cookie — необходимы для корректной работы Платформы
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-accent-500 mt-1.5 flex-shrink-0">—</span>
                    аналитические cookie — используются для анализа поведения Пользователей
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-accent-500 mt-1.5 flex-shrink-0">—</span>
                    функциональные cookie — сохраняют предпочтения Пользователя
                  </li>
                </ul>
                <p>
                  8.2. Пользователь может отключить cookie в настройках браузера, однако это может
                  повлиять на функциональность Платформы.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium text-white mb-4">9. Заключительные положения</h2>
                <p className="mb-3">
                  9.1. Настоящая Политика является неотъемлемой частью Пользовательского соглашения.
                </p>
                <p className="mb-3">
                  9.2. Администрация вправе вносить изменения в настоящую Политику.
                  Актуальная редакция размещается на данной странице.
                </p>
                <p>
                  9.3. По всем вопросам, связанным с обработкой персональных данных, Пользователь
                  может обратиться к Администрации через раздел{" "}
                  <Link to="/contact" className="text-primary-400 hover:text-primary-300 transition-colors">
                    Контакты
                  </Link>.
                </p>
              </section>

              <div className="pt-8 border-t border-neutral-800/50">
                <p className="text-lg font-extralight text-neutral-500">
                  Ознакомьтесь также с нашим{" "}
                  <Link to="/terms" className="text-primary-400 hover:text-primary-300 transition-colors">
                    Пользовательским соглашением
                  </Link>.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}
